import { waitForIDs } from 'app/copied/getIds';
import { addURLChangeListener, removeURLChangeListener } from 'app/utils/URLChangeDetector';
import { useEffect, useState } from 'react';

/**
 * Example of an excludeOnUrls whitelist:
 * const whitelist = [
 *   'https://www.example.com',
 *   'example.com',
 *   '*.example.com',
 *   'www.example.com/images/*',
 *   '^https:\\/www.example.com/api/v1/.*$',
 *   '/example/page*',
 * ];
 */

function checkIfAllowedOnUrl(url: string, whitelist: string[] = []) {
  // Parse the URL for easier comparison
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    // Handle relative URLs by using current origin
    urlObj = new URL(url, window.location.origin);
  }

  for (const pattern of whitelist) {
    // Handle exact URL matches
    if (pattern === url) return true;

    // Handle domain wildcards (*.example.com)
    if (pattern.startsWith('*.') && urlObj.hostname.endsWith(pattern.substring(1))) {
      return true;
    }

    // Handle path wildcards (www.example.com/images/* or https://www.example.com/images/*)
    if (pattern.includes('/*')) {
      // Check if it's a full URL pattern with protocol
      if (pattern.startsWith('http')) {
        try {
          const patternUrl = new URL(pattern.replace('/*', ''));
          if (urlObj.hostname === patternUrl.hostname && urlObj.pathname.startsWith(patternUrl.pathname)) {
            return true;
          }
        } catch (e) {
          console.error('Invalid URL pattern:', pattern);
        }
      } else {
        // Handle domain/path format without protocol
        const [domainPart, ...pathParts] = pattern.split('/');
        const pathPattern = pathParts.join('/').replace('*', '');

        if (urlObj.hostname === domainPart && urlObj.pathname.startsWith('/' + pathPattern)) {
          return true;
        }
      }
    }

    // Handle regex patterns (^https:\/\/...)
    if (pattern.startsWith('^') && pattern.endsWith('$')) {
      try {
        const regex = new RegExp(pattern);
        if (regex.test(url)) return true;
      } catch (e) {
        console.error('Invalid regex pattern:', pattern);
      }
    }

    // Handle simple domain matches (example.com)
    if (!pattern.includes('/') && !pattern.includes('*') && urlObj.hostname === pattern) {
      return true;
    }

    // Handle path patterns (/example/page*)
    if (pattern.startsWith('/')) {
      const pathRegex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`);
      if (pathRegex.test(urlObj.pathname)) return true;
    }
  }

  return false;
}

export function useExcludeTailorOnURL() {
  const [excludeTailor, setExcludeTailor] = useState(false);

  useEffect(() => {
    const handleExcludeTailor = async () => {
      const { excludeOnUrls } = await waitForIDs();
      if (checkIfAllowedOnUrl(window.location.href, excludeOnUrls)) {
        setExcludeTailor(true);
        return;
      }
      setExcludeTailor(false);
    };

    addURLChangeListener(handleExcludeTailor);
    handleExcludeTailor();

    return () => {
      removeURLChangeListener(handleExcludeTailor);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return excludeTailor;
}
